import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Password } from "primereact/password";
import React, { useCallback, useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { ApplicationUser } from "../models/user.model";
import { cachedMasterDataService } from "../service/cachedMasterData.service";
import { citiesService } from "../service/cities.service";
import { countryService } from "../service/country.service";
import { proposalService } from "../service/proposal.service";
import { roleService } from "../service/roles.service";
import { roomTypeService } from "../service/roomType.service";
import { statesService } from "../service/state.service";
import { userService } from "../service/user.service";
import SharedAutoComplete from "./Controls/SharedAutoComplete";
import "./UserDrawer.css";

// Helper functions for date handling without timezone issues
const parseDateFromApi = (dateValue: string | Date | null | undefined): Date | null => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const dateStr = String(dateValue).split('T')[0];
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const formatDateForApi = (date: Date | null | undefined): string | undefined => {
  if (!date || !(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface UserDrawerProps {
  show: boolean;
  onHide: () => void;
  user: ApplicationUser;
  onSuccess: () => Promise<void>;
  mode: "create" | "edit";
}

const UserDrawer: React.FC<UserDrawerProps> = ({
  show,
  onHide,
  user,
  onSuccess,
  mode,
}) => {
  const [form, setForm] = useState({
    id: user.id || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    userName: user.userName || user.email || "",
    email: user.email || "",
    phoneNumber: user.phoneNumber || "",
    rmcId: user.rmcId || null,
    selectedClientIds: user.rmcUserClientMappings
      ? user.rmcUserClientMappings.map((m) => String(m.clientId))
      : [],
    selectedClientId:
      user.rmcUserClientMappings && user.rmcUserClientMappings.length > 0
        ? String(user.rmcUserClientMappings[0].clientId)
        : "",
    proposalId: user.address?.[0]?.proposalId || "",
    isLocked: user.isLocked || false,
    password: "",
    roleId: user.roleId ? String(user.roleId) : "",
    doorNumber: user.address?.[0]?.doorNumber || "",
    buildingName: user.address?.[0]?.buildingName || "",
    address1: user.address?.[0]?.address1 || "",
    address2: user.address?.[0]?.address2 || "",
    checkIn: parseDateFromApi(user.address?.[0]?.checkIn),
    checkOut: parseDateFromApi(user.address?.[0]?.checkOut),
    noOfAdults: user.address?.[0]?.noOfAdults || (null as number | null),
    noOfChildrens: user.address?.[0]?.noOfChildrens || (null as number | null),
    cityId: user.address?.[0]?.cityId || 0,
    stateId: user.address?.[0]?.stateId || 0,
    countryId: user.address?.[0]?.countryId || 0,
    pincode: user.address?.[0]?.pincode || "",
    phone: user.address?.[0]?.phone?.toString() || "",
  });

  const [rmcs, setRmcs] = useState<{ id: string; name: string }[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [proposals, setProposals] = useState<{ id: string; name: string }[]>(
    []
  );
  const [filteredRmcs, setFilteredRmcs] = useState<
    { id: string; name: string }[]
  >([]);
  const [filteredClients, setFilteredClients] = useState<
    { id: string; name: string }[]
  >([]);
  const [filteredProposals, setFilteredProposals] = useState<
    { id: string; name: string }[]
  >([]);
  const [filteredCountries, setFilteredCountries] = useState<
    { label: string; value: number }[]
  >([]);
  const [filteredStates, setFilteredStates] = useState<
    { label: string; value: number }[]
  >([]);
  const [filteredCities, setFilteredCities] = useState<
    { id: number | string; name: string }[]
  >([]);
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  const [roomTypes, setRoomTypes] = useState<{ id: number; name: string }[]>(
    []
  );
  const [selectedRoomTypeIds, setSelectedRoomTypeIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [emailLiveError, setEmailLiveError] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const consultantRmcId =
    useSelector((state: any) => state.auth.rmcId) ??
    localStorage.getItem("rmcId") ??
    "";

  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const selectedRole = roles.find((r) => r.id === form.roleId);
  const [countries, setCountries] = useState<
    { label: string; value: number }[]
  >([]);
  const [states, setStates] = useState<{ label: string; value: number }[]>([]);
  const [cities, setCities] = useState<{ id: number | string; name: string }[]>(
    []
  );
  const [selectedCountry, setSelectedCountry] = useState<Number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);

  // Input text states for autocomplete to allow typing
  const [countryInputText, setCountryInputText] = useState<string>("");
  const [stateInputText, setStateInputText] = useState<string>("");
  const [cityInputText, setCityInputText] = useState<string>("");

  const isB2BUserRole = selectedRole?.name === "B2BUser";
  const isAdminRole =
    selectedRole?.name === "Administrator" ||
    selectedRole?.name === "Consultant" ||
    selectedRole?.name === "B2CUser";
  const isRMCAdminRole = selectedRole?.name === "RMCAdmin";

  const filteredRoles =
    userRole === "RMCAdmin"
      ? roles.filter(
          (role) =>
            role.name === "RMCAdmin" ||
            role.name === "RMCUser" ||
            role.name === "B2BUser"
        )
      : roles;

  useEffect(() => {
    if (consultantRmcId) {
      localStorage.setItem("rmcId", consultantRmcId);
    }
  }, [consultantRmcId]);

  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const countriesData = await countryService.getListItems();
        const countryOptions = countriesData.map((c: any) => ({
          label: c.name,
          value: c.id,
        }));
        setCountries(countryOptions);
      } catch (error) {
        // Error loading countries
      }
    };
    loadStaticData();
  }, []);

  useEffect(() => {
    if (show) {
      const fetchRMCs = async () => {
        try {
          const res = await cachedMasterDataService.getRmcListItems();
          setRmcs(
            res.map((rmc: any) => ({
              id: String(rmc.id ?? ""),
              name: rmc.name ?? "",
            }))
          );
        } catch (error) {
          setRmcs([]);
        }
      };
      fetchRMCs();
    }
  }, [show, consultantRmcId]);

  useEffect(() => {
    if (rmcs.length > 0) {
      if (mode === "edit" && user.rmcId) {
        setForm((prev) => ({
          ...prev,
          rmcId: user.rmcId ?? null,
        }));
      } else if (
        mode === "create" &&
        consultantRmcId &&
        userRole !== "Administrator"
      ) {
        setForm((prev) => ({
          ...prev,
          rmcId: Number(consultantRmcId),
        }));
      }
    }
  }, [rmcs, mode, user.rmcId, consultantRmcId, userRole]);

  useEffect(() => {
    if (mode === "edit" && form.countryId && form.countryId !== 0 && show && countries.length > 0) {
      const loadStates = async () => {
        try {
          setSelectedCountry(form.countryId);
          const statesData = await statesService.getByCountryId(form.countryId);
          const stateOptions = statesData.map((s: any) => ({
            label: s.name,
            value: s.id,
          }));
          setStates(stateOptions);
        } catch (error) {
          setStates([]);
        }
      };
      loadStates();
    }
  }, [mode, show, countries.length]);

  useEffect(() => {
    if (mode === "edit" && form.stateId && form.stateId !== 0 && show && states.length > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await citiesService.getByStateId(form.stateId);
          const cityOptions = citiesData.map((c: any) => ({
            id: c.id,
            name: c.name,
          }));
          setCities(cityOptions);
          setSelectedState(form.stateId);
        } catch (error) {
          setCities([]);
        }
      };
      loadCities();
    }
  }, [mode, show, states.length]);

  useEffect(() => {
    if (form.rmcId) {
      const fetchClientsByRMC = async () => {
        try {
          const res = await cachedMasterDataService.getClientsByRmcId(
            Number(form.rmcId) ?? consultantRmcId
          );
          setClients(
            res.map((client: any) => ({
              id: String(client.id ?? ""),
              name: client.name ?? "",
            }))
          );
        } catch (error) {
          setClients([]);
        }
      };
      fetchClientsByRMC();
    } else {
      setClients([]);
    }
  }, [form.rmcId]);

  useEffect(() => {
    if (form.rmcId && form.selectedClientId) {
      const fetchProposalsByRMCAndClient = async () => {
        try {
          const res = await proposalService.getProposalListItems(
            Number(form.rmcId),
            Number(form.selectedClientId)
          );
          const proposalOptions = res.map((proposal: any) => ({
            id: String(proposal.id ?? ""),
            name: proposal.name ?? "",
          }));
          setProposals(proposalOptions);

          if (
            form.proposalId &&
            proposalOptions.some((p: any) => p.id === String(form.proposalId))
          ) {
            setForm((prev) => ({
              ...prev,
              proposalId: String(form.proposalId),
            }));
          }
        } catch (error) {
          setProposals([]);
        }
      };
      fetchProposalsByRMCAndClient();
    } else {
      setProposals([]);
      setForm((prev) => ({ ...prev, proposalId: "" }));
    }
  }, [form.rmcId, form.selectedClientId]);

  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        const roomTypesData = await roomTypeService.getListItems();
        setRoomTypes(
          roomTypesData.map((rt: any) => ({ id: Number(rt.id), name: rt.name }))
        );
      } catch (error) {
        // Error loading room types
      }
    };
    loadRoomTypes();
  }, []);

  useEffect(() => {
    if (mode === "edit" && user.preferences && isB2BUserRole && roomTypes.length > 0) {
      const roomTypeIds = user.preferences.map((pref) => pref.roomTypeId);
      
      const validIds = roomTypeIds.filter(id => roomTypes.some(rt => rt.id === id));
      
      setSelectedRoomTypeIds(validIds);
    }
  }, [mode, user.preferences, isB2BUserRole, roomTypes]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await roleService.getAll();
        const mappedRoles = res.map((role: any) => ({
          id: String(role.id ?? "").toLowerCase(),
          name: role.name ?? "",
        }));
        const sortedRoles = mappedRoles.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setRoles(sortedRoles);
      } catch (error) {
        setRoles([]);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    if (mode === "edit" && user.roleId && roles.length > 0) {
      setForm((prev) => ({
        ...prev,
        roleId: String(user.roleId).toLowerCase(),
      }));
    }
  }, [mode, user.roleId, roles]);

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const name = e.target?.name || e.originalEvent?.target?.name;
    const value = e.target?.value || e.value;

    if (
      (name === "firstName" || name === "lastName") &&
      value &&
      !/^[A-Za-z]*$/.test(value)
    ) {
      return;
    }
    if (name === "phoneNumber" && value && !/^(\+?\d*)$/.test(value)) {
      return;
    }
    if (name === "email") {
      if (value && !isValidEmail(value)) {
        setEmailLiveError("Please enter a valid email address.");
      } else {
        setEmailLiveError("");
      }
    }
    setForm({ ...form, [name]: value });
  };

  const handleCheckInChange = (date: Date | null) => {
    setForm((prev) => ({ ...prev, checkIn: date }));
  };

  const handleCheckOutChange = (date: Date | null) => {
    setForm((prev) => ({ ...prev, checkOut: date }));
  };

  const handleRMCChange = async (
    e: React.ChangeEvent<HTMLSelectElement> | any
  ) => {
    const selectedRMCId = e.target?.value || e.value;
    setForm({
      ...form,
      rmcId: Number(selectedRMCId),
      selectedClientIds: [],
      selectedClientId: "",
      proposalId: "",
    });
    try {
      const res = await cachedMasterDataService.getClientsByRmcId(
        Number(selectedRMCId)
      );
      setClients(
        res.map((client: any) => ({
          id: String(client.id ?? ""),
          name: client.name ?? "",
        }))
      );
    } catch (error) {
      setClients([]);
    }
    setProposals([]);
  };

  const searchRmcs = (event: any) => {
    const query = event.query.toLowerCase();
    const filtered = rmcs.filter((rmc) =>
      rmc.name.toLowerCase().includes(query)
    );
    setFilteredRmcs(filtered);
  };

  const searchClients = (event: any) => {
    const query = event.query.toLowerCase();
    const filtered = clients.filter((client) =>
      client.name.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  const searchProposals = (event: any) => {
    const query = event.query.toLowerCase();
    const filtered = proposals.filter((proposal) =>
      proposal.name.toLowerCase().includes(query)
    );
    setFilteredProposals(filtered);
  };

  const searchCountries = (event: any) => {
    setCountryInputText(event.query); // Update input text as user types
    const query = event.query.toLowerCase();
    const filtered = countries.filter((country) =>
      country.label.toLowerCase().includes(query)
    );
    setFilteredCountries(filtered);
  };

  const searchStates = (event: any) => {
    setStateInputText(event.query); // Update input text as user types
    const query = event.query.toLowerCase();
    const filtered = states.filter((state) =>
      state.label.toLowerCase().includes(query)
    );
    setFilteredStates(filtered);
  };

  const searchCities = (event: any) => {
    setCityInputText(event.query); // Update input text as user types
    const query = event.query.toLowerCase();
    const filtered = cities.filter((city) =>
      city.name.toLowerCase().includes(query)
    );
    setFilteredCities(filtered);
  };

  const handleCountryChange = useCallback(async (countryId: number) => {
    setSelectedCountry(countryId);
    setForm((prev) => ({
      ...prev,
      countryId: countryId,
      stateId: 0,
      cityId: 0,
    }));
    setSelectedState(null);
    setCities([]);

    try {
      const statesData = await statesService.getByCountryId(countryId);
      const stateOptions = statesData.map((s: any) => ({
        label: s.name,
        value: s.id,
      }));
      setStates(stateOptions);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
    }
  }, []);

  const handleStateChange = useCallback(async (stateId: number) => {
    setSelectedState(stateId);
    setForm((prev) => ({ ...prev, stateId: stateId, cityId: 0 }));

    try {
      const citiesData = await citiesService.getByStateId(stateId);
      const cityOptions = citiesData.map((c: any) => ({
        id: c.id,
        name: c.name,
      }));
      setCities(cityOptions);
    } catch (error) {
      console.error("Error loading cities:", error);
      setCities([]);
    }
  }, []);

  const handleCityChange = (cityId: number) => {
    setForm((prev) => ({ ...prev, cityId: cityId }));
  };

  // Sync input text states with selected values
  useEffect(() => {
    const selectedCountryObj = countries.find(
      (c) => c.value === selectedCountry || c.value === form.countryId
    );
    setCountryInputText(selectedCountryObj?.label || "");
  }, [selectedCountry, form.countryId, countries]);

  useEffect(() => {
    const selectedStateObj = states.find(
      (s) => s.value === selectedState || s.value === form.stateId
    );
    setStateInputText(selectedStateObj?.label || "");
  }, [selectedState, form.stateId, states]);

  useEffect(() => {
    const selectedCityObj = cities.find((c) => c.id === form.cityId);
    setCityInputText(selectedCityObj?.name || "");
  }, [form.cityId, cities]);

  // Clear input texts when drawer closes
  useEffect(() => {
    if (!show) {
      setCountryInputText("");
      setStateInputText("");
      setCityInputText("");
    }
  }, [show]);

  const handleClose = () => {
    onHide();
  };

  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!form.firstName) newErrors.firstName = "First name is required.";
    if (!form.lastName) newErrors.lastName = "Last name is required.";
    if (!form.email) newErrors.email = "Email is required.";
    if (form.email && !isValidEmail(form.email))
      newErrors.email = "Please enter a valid email address.";
    if (!form.roleId) newErrors.roleId = "Role is required.";
    if (isRMCAdminRole && !form.rmcId) newErrors.rmcId = "RMC is required for RMC Admin role.";
    if (isB2BUserRole) {
      if (!form.rmcId) newErrors.rmcId = "RMC is required for B2B User role.";
      if (!form.selectedClientId) newErrors.selectedClientId = "Client is required for B2B User role.";
    }
    if (mode === "create" && !form.password) {
      newErrors.password = "Password is required.";
    } else if (mode === "create" && form.password) {
      // Backend password validation: minimum 6 characters, must contain special character (!@#$%^&*)
      const hasSpecialChar = /[!@#$%^&*]/.test(form.password);
      const hasMinLength = form.password.length >= 6;
      
      if (!hasMinLength && !hasSpecialChar) {
        newErrors.password = "Password must be at least 6 characters and contain at least one special character (!@#$%^&*)";
      } else if (!hasMinLength) {
        newErrors.password = "Password must be at least 6 characters";
      } else if (!hasSpecialChar) {
        newErrors.password = "Password must contain at least one special character (!@#$%^&*)";
      }
    }

    if (isB2BUserRole && selectedRoomTypeIds.length === 0) {
      newErrors.roomTypePreferences =
        "At least one room type preference is required.";
    }

    if (isB2BUserRole) {
      if (form.checkIn && form.checkOut && form.checkIn >= form.checkOut) {
        newErrors.checkOut = "Check-out date must be after check-in date";
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    try {
      if (mode === "create") {
        const payload: Partial<ApplicationUser> = {
          firstName: form.firstName,
          lastName: form.lastName,
          userName: form.userName || form.email,
          email: form.email,
          phoneNumber: form.phoneNumber,
          roleId: form.roleId,
          roleName: selectedRole?.name,
          isLocked: form.isLocked,
          password: form.password,
          rmcId: form.rmcId ? Number(form.rmcId) : undefined,
          clientIds:
            form.selectedClientIds.length > 0
              ? form.selectedClientIds.map(Number)
              : undefined,
          clientId: undefined,
          id: undefined,
        };

        if (isB2BUserRole) {
          payload.address = [
            {
              doorNumber: form.doorNumber,
              buildingName: form.buildingName,
              address1: form.address1,
              address2: form.address2,
              checkIn: formatDateForApi(form.checkIn) || undefined,
              checkOut: formatDateForApi(form.checkOut) || undefined,
              noOfAdults: form.noOfAdults || undefined,
              noOfChildrens: form.noOfChildrens || undefined,
              cityId: form.cityId,
              pincode: form.pincode,
              phone: form.phone,
              proposalId: form.proposalId ? Number(form.proposalId) : undefined,
              type: "Primary",
            },
          ];

          if (selectedRoomTypeIds.length > 0) {
            payload.preferences = selectedRoomTypeIds.map((roomTypeId) => ({
              roomTypeId: roomTypeId,
              name: roomTypes.find((rt) => rt.id === roomTypeId)?.name,
            }));
          }
        }

        await userService.create(payload);
      } else {
        const updatePayload: any = {
          ...user,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          userName: form.email,
          phoneNumber: form.phoneNumber,
          roleId: form.roleId ?? null,
          isLocked: form.isLocked,
          rmcId: form.rmcId ?? null,
          rmcUserClientMappings:
            form.selectedClientIds.length > 0
              ? form.selectedClientIds.map((clientId) => ({
                  rmcId: form.rmcId ?? null,
                  clientId: Number(clientId),
                }))
              : [],
          address: isB2BUserRole
            ? [
                {
                  id: user.address?.[0]?.id ?? 0,
                  userId: user.id?.toString(),
                  doorNumber: form.doorNumber,
                  buildingName: form.buildingName,
                  address1: form.address1,
                  address2: form.address2,
                  checkIn: formatDateForApi(form.checkIn) || undefined,
                  checkOut: formatDateForApi(form.checkOut) || undefined,
                  noOfAdults: form.noOfAdults || undefined,
                  noOfChildrens: form.noOfChildrens || undefined,
                  countryId: form.countryId,
                  stateId: form.stateId,
                  cityId: form.cityId,
                  pincode: form.pincode,
                  phone: form.phone,
                  type: "Primary",
                  proposalId: form.proposalId
                    ? Number(form.proposalId)
                    : undefined,
                },
              ]
            : [],
        };

        if (isB2BUserRole) {
          updatePayload.preferences = selectedRoomTypeIds.map((roomTypeId) => ({
            roomTypeId: roomTypeId,
            name: roomTypes.find((rt) => rt.id === roomTypeId)?.name,
          }));
        }

        if (form.password) updatePayload.password = form.password;

        await userService.update(user.id!, updatePayload);
      }

      await onSuccess();
      Swal.fire({
        icon: "success",
        title: `User ${
          mode === "create" ? "created" : "updated"
        } successfully!`,
        timer: 2000,
        toast: true,
        position: "bottom",
        showConfirmButton: false,
      });
      handleClose();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: error.message || "Failed to save user",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {show && <div className="user-drawer-overlay" onClick={handleClose} />}

      {/* Drawer */}
      <div className={`user-drawer ${show ? "show" : ""}`}>
        {/* Header */}
        <div className="user-drawer-header">
          <div className="user-drawer-title">
            {mode === "create" ? (
              <Icon.PersonPlus size={20} className="me-2 text-white" />
            ) : (
              <Icon.PersonGear size={20} className="me-2 text-white" />
            )}
            <h4 className="mb-0">
              {mode === "create" ? "Create User" : "Edit User"}
            </h4>
          </div>
          <button
            className="user-drawer-close"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            <Icon.X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="user-drawer-body">
          {/* Basic Information Section */}
          <div className="user-drawer-section">
            <div className="user-section-header">
              <Icon.PersonBadge size={18} className="me-2 text-success" />
              <h6 className="mb-0">Basic Information</h6>
            </div>

            {/* Form fields will be added here */}
            <div className="user-form-grid">
              <div className="user-form-field">
                <label className="user-field-label">
                  First Name <span className="text-danger">*</span>
                </label>
                <InputText
                  value={form.firstName}
                  onChange={(e) =>
                    setForm({ ...form, firstName: e.target.value })
                  }
                  className={`user-input ${errors.firstName ? "p-invalid" : ""}`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <small className="p-error">{errors.firstName}</small>
                )}
              </div>

              <div className="user-form-field">
                <label className="user-field-label">
                  Last Name <span className="text-danger">*</span>
                </label>
                <InputText
                  value={form.lastName}
                  onChange={(e) =>
                    setForm({ ...form, lastName: e.target.value })
                  }
                  className={`user-input ${errors.lastName ? "p-invalid" : ""}`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <small className="p-error">{errors.lastName}</small>
                )}
              </div>

              <div className="user-form-field">
                <label className="user-field-label">
                  Email <span className="text-danger">*</span>
                </label>
                <InputText
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`user-input ${errors.email || emailLiveError ? "p-invalid" : ""}`}
                  placeholder="Enter email"
                />
                {errors.email && (
                  <small className="p-error">{errors.email}</small>
                )}
                {emailLiveError && !errors.email && (
                  <small className="p-error">{emailLiveError}</small>
                )}
              </div>

              <div className="user-form-field">
                <label className="user-field-label">Phone Number</label>
                <InputText
                  value={form.phoneNumber}
                  onChange={(e) =>
                    setForm({ ...form, phoneNumber: e.target.value })
                  }
                  className={`user-input ${errors.phoneNumber ? "p-invalid" : ""}`}
                  placeholder="Enter phone number"
                />
                {errors.phoneNumber && (
                  <small className="p-error">{errors.phoneNumber}</small>
                )}
              </div>

              {mode === "create" && (
                <div className="user-form-field">
                  <label className="user-field-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <Password
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                    className={`user-input w-100 ${errors.password ? "p-invalid" : ""}`}
                    placeholder="Enter password"
                    toggleMask
                  />
                  {errors.password && (
                    <small className="p-error">{errors.password}</small>
                  )}
                  <small className="text-muted d-block mt-1">
                    {/* Min 6 characters, must include a special character (!@#$%^&*) */}
                  </small>
                </div>
              )}

              <div className="user-form-field">
                <label className="user-field-label">
                  Role <span className="text-danger">*</span>
                </label>
                <Dropdown
                  value={form.roleId}
                  options={filteredRoles}
                  onChange={(e: any) => setForm({ ...form, roleId: e.value })}
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select role"
                  className={`user-input w-100 ${errors.roleId ? "p-invalid" : ""}`}
                  appendTo="self"
                />
                {errors.roleId && (
                  <small className="p-error">{errors.roleId}</small>
                )}
              </div>
            </div>
          </div>

          {/* Access Level Section (for non-Admin roles) */}
          {!isAdminRole && (
            <div className="user-drawer-section">
              <div className="user-section-header">
                <Icon.Building size={18} className="me-2 text-success" />
                <h6 className="mb-0">Access Level</h6>
              </div>

              <div className="user-form-grid">
                <div className="user-form-field">
                  <label className="user-field-label">
                    RMC {(isRMCAdminRole || isB2BUserRole) && <span className="text-danger">*</span>}
                  </label>
                  <SharedAutoComplete
                    value={
                      form.rmcId
                        ? rmcs.find((r) => r.id === String(form.rmcId))?.name ||
                          ""
                        : ""
                    }
                    suggestions={filteredRmcs}
                    completeMethod={searchRmcs}
                    field="name"
                    onChange={(e) => {
                      if (e.value && e.value.id) {
                        handleRMCChange({
                          target: { value: e.value.id },
                        } as any);
                      }
                    }}
                    placeholder="Search RMC"
                    disabled={userRole === "RMCAdmin"}
                    className={`user-input ${errors.rmcId ? "p-invalid" : ""}`}
                    id="user-drawer-rmc"
                  />
                  {errors.rmcId && (
                    <small className="p-error">{errors.rmcId}</small>
                  )}
                </div>

                {!isRMCAdminRole && (
                  <div className="user-form-field">
                    <label className="user-field-label">
                      {selectedRole?.name === "RMCUser" ? "Clients" : "Client"}
                      {isB2BUserRole && <span className="text-danger">*</span>}
                    </label>
                    {selectedRole?.name === "RMCUser" ? (
                      <MultiSelect
                        value={form.selectedClientIds}
                        options={clients.map((client) => ({
                          label: client.name,
                          value: client.id,
                        }))}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            selectedClientIds: e.value,
                            selectedClientId:
                              e.value.length > 0 ? e.value[0] : "",
                            proposalId: "",
                          }))
                        }
                        placeholder="Select Clients"
                        display="chip"
                        filter
                        className="user-input w-100"
                        appendTo="self"
                      />
                    ) : (
                      <SharedAutoComplete
                        value={
                          form.selectedClientId
                            ? clients.find(
                                (c) => c.id === form.selectedClientId
                              )?.name || ""
                            : ""
                        }
                        suggestions={filteredClients}
                        completeMethod={searchClients}
                        field="name"
                        onChange={(e: any) => {
                          if (e.value?.id) {
                            setForm((prev) => ({
                              ...prev,
                              selectedClientIds: [e.value!.id],
                              selectedClientId: e.value!.id,
                              proposalId: "",
                            }));
                          }
                        }}
                        placeholder="Search Client"
                        className={`user-input ${errors.selectedClientId ? "p-invalid" : ""}`}
                        id="user-drawer-client"
                      />
                    )}
                    {errors.selectedClientId && (
                      <small className="p-error">{errors.selectedClientId}</small>
                    )}
                  </div>
                )}

                {/* Proposal field moved here from Address Details */}
                {isB2BUserRole && (
                  <div className="user-form-field">
                    <label className="user-field-label">Proposal</label>
                    <SharedAutoComplete
                      value={
                        form.proposalId
                          ? proposals.find(
                              (p) => p.id === String(form.proposalId)
                            )?.name || ""
                          : ""
                      }
                      suggestions={filteredProposals}
                      completeMethod={searchProposals}
                      field="name"
                      onChange={(e: any) => {
                        if (e.value?.id) {
                          setForm((prev) => ({
                            ...prev,
                            proposalId: e.value!.id,
                          }));
                        }
                      }}
                      placeholder="Search Proposal"
                      disabled={!form.rmcId || !form.selectedClientId}
                      className="user-input"
                      id="user-drawer-proposal"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* B2B User Address and Booking Details Section */}
          {isB2BUserRole && (
            <>
              <div className="user-drawer-section">
                <div className="user-section-header">
                  <Icon.GeoAlt size={18} className="me-2 text-success" />
                  <h6 className="mb-0">Address Details</h6>
                </div>

                <div className="user-form-grid">
                  <div className="user-form-field">
                    <label className="user-field-label">Door Number</label>
                    <InputText
                      name="doorNumber"
                      value={form.doorNumber}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter door number"
                    />
                  </div>

                  <div className="user-form-field user-form-field-full">
                    <label className="user-field-label">Building Name</label>
                    <InputText
                      name="buildingName"
                      value={form.buildingName}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter building name"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Address Line 1</label>
                    <InputText
                      name="address1"
                      value={form.address1}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter address line 1"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Address Line 2</label>
                    <InputText
                      name="address2"
                      value={form.address2}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter address line 2"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Country</label>
                    <SharedAutoComplete
                      value={countryInputText}
                      suggestions={filteredCountries}
                      completeMethod={searchCountries}
                      field="label"
                      onChange={(e: any) => {
                        // Handle text input or selection
                        if (typeof e.value === "string") {
                          setCountryInputText(e.value);
                        } else if (
                          e.value &&
                          typeof e.value === "object" &&
                          "value" in e.value
                        ) {
                          handleCountryChange(e.value.value as number);
                          setCountryInputText(e.value.label);
                        }
                      }}
                      onSelect={(e: any) => {
                        // Handle when item is selected from suggestions
                        if (e.value && "value" in e.value) {
                          handleCountryChange(e.value.value as number);
                          setCountryInputText(e.value.label);
                        }
                      }}
                      placeholder="Search Country"
                      className="user-input"
                      id="user-drawer-country"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">State</label>
                    <SharedAutoComplete
                      value={stateInputText}
                      suggestions={filteredStates}
                      completeMethod={searchStates}
                      field="label"
                      onChange={(e: any) => {
                        // Handle text input or selection
                        if (typeof e.value === "string") {
                          setStateInputText(e.value);
                        } else if (
                          e.value &&
                          typeof e.value === "object" &&
                          "value" in e.value
                        ) {
                          handleStateChange(e.value.value as number);
                          setStateInputText(e.value.label);
                        }
                      }}
                      onSelect={(e: any) => {
                        // Handle when item is selected from suggestions
                        if (e.value && "value" in e.value) {
                          handleStateChange(e.value.value as number);
                          setStateInputText(e.value.label);
                        }
                      }}
                      placeholder="Search State"
                      disabled={!selectedCountry}
                      className="user-input"
                      id="user-drawer-state"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">City</label>
                    <SharedAutoComplete
                      value={cityInputText}
                      suggestions={filteredCities}
                      completeMethod={searchCities}
                      field="name"
                      onChange={(e: any) => {
                        // Handle text input or selection
                        if (typeof e.value === "string") {
                          setCityInputText(e.value);
                        } else if (
                          e.value &&
                          typeof e.value === "object" &&
                          "id" in e.value
                        ) {
                          handleCityChange(e.value.id as number);
                          setCityInputText(e.value.name);
                        }
                      }}
                      onSelect={(e: any) => {
                        // Handle when item is selected from suggestions
                        if (e.value && "id" in e.value) {
                          handleCityChange(e.value.id as number);
                          setCityInputText(e.value.name);
                        }
                      }}
                      placeholder="Search City"
                      disabled={cities.length === 0}
                      className="user-input"
                      id="user-drawer-city"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Pincode</label>
                    <InputText
                      name="pincode"
                      value={form.pincode}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter pincode"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Phone</label>
                    <InputText
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="user-input"
                      placeholder="Enter phone"
                    />
                  </div>
                </div>
              </div>

              <div className="user-drawer-section">
                <div className="user-section-header">
                  <Icon.Calendar3 size={18} className="me-2 text-success" />
                  <h6 className="mb-0">Booking Details</h6>
                </div>

                <div className="user-form-grid">
                  <div className="user-form-field">
                    <label className="user-field-label">Check-in Date</label>
                    <Calendar
                      value={form.checkIn}
                      onChange={(e) =>
                        handleCheckInChange(e.value as Date | null)
                      }
                      dateFormat="dd/mm/yy"
                      placeholder="Select check-in date"
                      minDate={new Date()}
                      className="user-input w-100"
                      appendTo="self"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Check-out Date</label>
                    <Calendar
                      value={form.checkOut}
                      onChange={(e) =>
                        handleCheckOutChange(e.value as Date | null)
                      }
                      dateFormat="dd/mm/yy"
                      placeholder="Select check-out date"
                      minDate={form.checkIn || new Date()}
                      className="user-input w-100"
                      appendTo="self"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">Number of Adults</label>
                    <InputNumber
                      value={form.noOfAdults}
                      onValueChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          noOfAdults: e.value ?? null,
                        }))
                      }
                      placeholder="Enter number of adults"
                      min={1}
                      max={10}
                      className="user-input w-100"
                    />
                  </div>

                  <div className="user-form-field">
                    <label className="user-field-label">
                      Number of Children
                    </label>
                    <InputNumber
                      value={form.noOfChildrens}
                      onValueChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          noOfChildrens: e.value ?? null,
                        }))
                      }
                      placeholder="Enter number of children"
                      min={0}
                      max={10}
                      className="user-input w-100"
                    />
                  </div>

                  <div className="user-form-field user-form-field-full">
                    <label className="user-field-label">
                      Preferred Room Types{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <MultiSelect
                      value={selectedRoomTypeIds}
                      onChange={(e) => {
                        const validRoomTypeIds = (e.value as number[]).filter((id: number) => 
                          roomTypes.some(rt => rt.id === id)
                        );
                        const uniqueRoomTypeIds: number[] = Array.from(new Set(validRoomTypeIds));
                        setSelectedRoomTypeIds(uniqueRoomTypeIds);
                      }}
                      options={roomTypes.map((rt) => ({
                        label: rt.name,
                        value: rt.id,
                      }))}
                      placeholder="Select preferred room types"
                      maxSelectedLabels={3}
                      className="user-input w-100"
                      appendTo="self"
                    />
                    <small className="text-muted">
                      Select the room types you prefer for your accommodations
                    </small>
                    {errors.roomTypePreferences && (
                      <div className="text-danger mt-1">
                        {errors.roomTypePreferences}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Lock Status Section */}
          <div className="user-drawer-section">
            <div className="user-section-header">
              <Icon.ShieldLock size={18} className="me-2 text-success" />
              <h6 className="mb-0">Account Status</h6>
            </div>

            <div className="user-form-field">
              <div className="d-flex align-items-center">
                <InputSwitch
                  checked={form.isLocked}
                  onChange={(e) => setForm({ ...form, isLocked: e.value })}
                />
                <label className="user-field-label mb-0 ms-3">
                  Account Locked
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="user-drawer-footer">
          <Button
            label="Cancel"
            className="user-btn-cancel"
            onClick={handleClose}
            disabled={saving}
          />
          <Button
            label={mode === "create" ? "Create User" : "Update User"}
            className="user-btn-save"
            onClick={handleSave}
            loading={saving}
            icon={mode === "create" ? "pi pi-plus" : "pi pi-check"}
          />
        </div>
      </div>
    </>
  );
};

export default UserDrawer;
